import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';

import { useAggregateClusters } from '@/api/generated';
import type { BarChartClickHandler } from '@/components/charts/types';
import Header from '@/components/header';
import { Spinner } from '@/components/ui/spinner';
import { ClusterCharts } from '@/routes/(app)/(clusters)/components/cluster-charts';
import ClusterMetrics from '@/routes/(app)/(clusters)/components/cluster-metrics';

const route = getRouteApi('/app/clusters/compare');

const Compare = () => {
  const { clusterIds } = route.useSearch();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useAggregateClusters({ clusterIds });

  const isEval = useMemo(
    () => data?.some((cluster) => cluster.dataset.isEval) ?? false,
    [data]
  );

  const handleClick: BarChartClickHandler = useCallback((_data) => {
    setSelected(_data.id);
  }, []);

  if (isLoading || !data) return <Spinner />;

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-6'>
        <Header
          title={t('comparison.title')}
          description={t('comparison.description', {
            count: data.length,
          })}
        />
      </div>

      {isEval && (
        <ClusterCharts
          onClick={handleClick}
          data={data}
        />
      )}

      <div className='flex w-full flex-row flex-wrap gap-6'>
        {data.map((cluster) => (
          <ClusterMetrics
            key={cluster.id}
            cluster={cluster}
            active={selected === cluster.id}
            isEval={isEval}
          />
        ))}
      </div>
    </div>
  );
};

export default Compare;
